import React, { ReactNode, useState, useEffect } from 'react';
import CandidateSidebar from './navigation/CandidateSidebar';
import { DashboardHeader } from '../../shared/components';
import type { SearchConfig } from '../../shared/components';
import { usePushNotifications } from '../../shared/hooks/usePushNotifications';

interface CandidateLayoutProps {
    children: ReactNode;
    showHeader?: boolean;
    headerTitle?: string;
    headerSubtitle?: string;
    isHomePage?: boolean;
    searchConfig?: SearchConfig;
    /** When true, content area is fixed to viewport height (for messaging/full-screen panels) */
    fullHeight?: boolean;
}

/**
 * Main layout component for candidate dashboard pages
 * Includes sidebar navigation, shared DashboardHeader component
 * Mirrors the AdminLayout structure for consistency
 */
const CandidateLayout: React.FC<CandidateLayoutProps> = ({
    children,
    showHeader = true,
    headerTitle,
    headerSubtitle,
    isHomePage = false,
    searchConfig,
    fullHeight = false,
}) => {
    // 7l: Request browser push notification permission on first load
    usePushNotifications();

    const [userName, setUserName] = useState('');
    // Persist sidebar collapsed state in localStorage
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('candidateSidebarCollapsed');
        return saved === 'true';
    });
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Update localStorage when sidebar state changes
    const handleSidebarToggle = () => {
        const newState = !sidebarCollapsed;
        setSidebarCollapsed(newState);
        localStorage.setItem('candidateSidebarCollapsed', String(newState));
    };

    // Get user name from localStorage
    useEffect(() => {
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                const firstName = userData.first_name || userData.firstName || userData.name || userData.email?.split('@')[0] || 'User';
                setUserName(firstName);
            } catch (error) {
                console.error('Failed to parse user data:', error);
            }
        }
    }, []);

    // Default header texts
    const defaultTitle = `Welcome back, ${userName}!`;
    const defaultSubtitle = "Here's what's happening in your career space today.";

    return (
        <div className="min-h-screen bg-[#F1F3F4] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {/* Sidebar */}
            <CandidateSidebar
                isCollapsed={sidebarCollapsed}
                onToggle={handleSidebarToggle}
                isMobileOpen={mobileSidebarOpen}
                onMobileClose={() => setMobileSidebarOpen(false)}
            />

            {/* Mobile Menu Button - Fixed Position */}
            <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-30 w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-gray-50 shadow-md border border-gray-200/80 transition-all duration-200 hover:shadow-lg"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#015FA6]">
                    <line x1="3" x2="21" y1="6" y2="6" />
                    <line x1="3" x2="21" y1="12" y2="12" />
                    <line x1="3" x2="21" y1="18" y2="18" />
                </svg>
            </button>

            {/* Main Content Area */}
            <div className={`
                transition-all duration-300 flex flex-col
                ${fullHeight ? 'h-screen overflow-hidden pt-2 lg:pt-8' : 'pt-2 lg:pt-8 min-h-screen overflow-y-auto'}
                ${sidebarCollapsed ? 'lg:ml-[100px]' : 'lg:ml-[280px]'}
            `}>
                {/* Shared Dashboard Header */}
                {showHeader && (
                    <div className="flex-shrink-0">
                        <DashboardHeader
                            title={headerTitle || defaultTitle}
                            subtitle={headerSubtitle || defaultSubtitle}
                            titleColor={isHomePage ? 'blue' : 'black'}
                            searchConfig={searchConfig}
                        />
                    </div>
                )}

                {/* Page Content */}
                <main className={`animate-fade-in-up ${fullHeight ? 'flex-1 min-h-0 overflow-hidden px-4 lg:px-6 pb-4 lg:pb-6' : 'px-4 lg:px-6 pb-4 lg:pb-8 flex-1 flex flex-col min-h-0'}`}>
                    {children}
                </main>
            </div>
        </div>
    );
};

export default CandidateLayout;
